import { useCallback, useMemo, useState } from "react";
import {
  Box,
  IconButton,
  TextField,
} from "@mui/material";

import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import { ImageItem } from "types/types/Types";
import { useAuth } from "context/AuthContext";
import { useDeleteCaseImage, useUpdateProcedureImage } from "hooks/useProcedure";
import { snackbarService } from "services/snackbarService";
import CustomText from "components/common/CustomText/CustomText";
import { colors } from "styles/colors";
import EditIcon from "icons/EditIcon";
import CustomDialog from "components/dialogs/CustomDialog";
import CustomButton from "components/buttons/CustomButton/CustomButton";
import { useDeleteRegistryPatientImage, useUpdateRegistryPatientImage } from "hooks/useRegistryPatient";
import { useParams } from "react-router-dom";


interface Props {
  index: number;
  item: ImageItem;
  handleRename: (index: number, value: string) => void;
  handleRemove: (index: number) => void;
  isEditMode: boolean;
  onPreview: () => void;
}


export default function CaseImage({
  item,
  handleRename,
  index,
  handleRemove,
  isEditMode,
  onPreview,
}: Props) {
  const { user } = useAuth();
  const { registryId, patientId } = useParams();

  const [localName, setLocalName] = useState<string>(item.image_name);
  const [renameModalVisible, setRenameModalVisible] = useState<boolean>(false);
  const [deleteOpen, setDeleteOpen] = useState<boolean>(false);


  const { mutateAsync: updateMutationCase, isLoading: isCaseUpdating } = useUpdateProcedureImage(
    (res: any) => {
      const { data, status_code: statusCode } = res;

      if (statusCode == 200) {
        snackbarService.showSnackbar("Image has been updated successfully", "success");
      } else {
        snackbarService.showSnackbar(data, 'error');
      }

    },
    (err: any) => snackbarService.showSnackbar(err, "error")
  );

  const { mutateAsync: updateMutationRegistry, isLoading: isRegistryUpdating } = useUpdateRegistryPatientImage(
    (res: any) => {
      const { data, status_code: statusCode } = res;

      if (statusCode == 200) {
        snackbarService.showSnackbar("Image has been updated successfully", "success");
      } else {
        snackbarService.showSnackbar(data, 'error');
      }

    },
    (err: any) => snackbarService.showSnackbar(err, "error")
  );

  const { mutate: deleteMutationCase, isLoading: isCaseDeleting } = useDeleteCaseImage(
    () => {
      handleRemove(index);
      setDeleteOpen(false);
      snackbarService.showSnackbar("Image has been deleted successfully", "success");
    },
    (err: any) => snackbarService.showSnackbar(err, "error")
  );

  const { mutate: deleteMutationRegistry, isLoading: isRegistryDeleting } = useDeleteRegistryPatientImage(
    () => {
      handleRemove(index);
      setDeleteOpen(false);
      snackbarService.showSnackbar("Image has been deleted successfully", "success");
    },
    (err: any) => snackbarService.showSnackbar(err, "error")
  );

  //   const { mutate: downloadMutation, isLoading: isDownloading } = useDownloadProcedureImage(
  //     () => {
  //       handleCloseOptions();
  //       appService.showToast("Image downloaded successfully", "success");
  //     },
  //     (err: any) => appService.showToast(err, "error")
  //   );

  const isValidName = useMemo(() => localName.trim() !== "", [localName]);

  const handleNameSave = useCallback(async () => {
    if (!isValidName) return;

    if (item?.id) {
      if (registryId && patientId) {
        await updateMutationRegistry({
          dto: {
            registryId,
            patientId,
          },
          image: { ...item, image_name: localName },
        } as any);
      } else {
        await updateMutationCase({
          userId: user.id,
          procedureId: item.procedure,
          image: { ...item, image_name: localName },
        } as any);
      }
    }
    setRenameModalVisible(false);
    handleRename(index, localName);
  }, [handleRename, index, isValidName, item, localName, updateMutationCase, user.id]);

  const handleDeleteImage = useCallback(() => {
    if (registryId && patientId) {
      deleteMutationRegistry({
        dto: {
          registryId,
          patientId,
        },
        imageId: item?.id,
      } as any);
    } else {
      deleteMutationCase({
        userId: user.id,
        procedureId: item.procedure,
        imageId: item?.id,
      } as any);
    }
  }, [deleteMutationCase, item?.id, item.procedure, user.id]);


  const handleRemoveImage = useCallback(() => {
    handleRemove(index);
  }, [handleRemove, index]);


  const isSubmitting = useMemo(() => {
    return isCaseUpdating || isCaseDeleting || isRegistryUpdating || isRegistryDeleting;
  }, [isCaseUpdating, isCaseDeleting, isRegistryUpdating, isRegistryDeleting]);

  return (
    <>
      {/* Image Container */}
      <Box
        onClick={onPreview}
        sx={{
          position: "relative",
          width: "100%",
          borderRadius: 2,
          overflow: "hidden",
          height: 190,
          bgcolor: "grey.200",
        }}
      >
        {/* Image */}
        <Box
          component="img"
          src={item.uri}
          alt={item.image_name}
          sx={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            display: "block",
          }}
        />

        {/* Gradient overlay (transparent -> black 0.6) */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(to bottom, rgba(0,0,0,0) 40%, rgba(0,0,0,0.6) 100%)",
          }}
        />

        {/* Name (clickable to rename) */}
        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            setRenameModalVisible(true);
          }}
          disabled={!isEditMode}
          sx={{
            position: "absolute",
            left: 10,
            bottom: 10,
            maxWidth: "calc(100% - 60px)",
            minWidth: 0,
            padding: 0,
            borderRadius: 1,
            color: colors.white,
            justifyContent: "flex-start",
            gap: 0.75,
          }}
        >
          {isEditMode && <EditIcon size={14} color={colors.white} />}
          <Box
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            <CustomText text={item.image_name} color={colors.white} />
          </Box>
        </IconButton>


        {/* Right-side action (options or remove) */}
        {isEditMode && (
          <Box
            sx={{
              position: "absolute",
              top: 10,
              right: 10,
              display: "flex",
              alignItems: "center",
            }}
          >
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                setDeleteOpen(true);
              }}
              aria-label="remove"
              disableRipple
              sx={{
                padding: 0,
                minWidth: 0,
                minHeight: 0,
                display: "flex",
              }}
            >
              <RemoveCircleOutlineIcon
                sx={{
                  color: colors.error,
                  fontSize: 20,
                  display: "block",
                }}
              />
            </IconButton>
          </Box>
        )}

      </Box>

      {/* Remove / Delete Dialog */}
      <CustomDialog
        open={deleteOpen}
        title={item?.id ? "Delete Image" : "Remove Image"}
        message={item?.id
          ? "Note: Deleting this image is permanent and cannot be undone."
          : "Are you sure you want to remove this image?"}
        buttons={[
          <CustomButton
            key={'cancel'}
            variant='outlined'
            text='Cancel'
            textColor='error'
            onClick={() => setDeleteOpen(false)}
            height={35}
          />,
          <CustomButton
            key={'delete'}
            variant='filled'
            color='error'
            text={item?.id ? "Delete Anyway" : "Remove"}
            onClick={() => (item?.id ? handleDeleteImage() : handleRemoveImage())}
            height={35}
            loading={isSubmitting}
          />,
        ]}
      />

      {/* Rename Dialog */}
      <CustomDialog
        open={renameModalVisible}
        title={""}
        message={""}
        content={
          <TextField
            autoFocus
            fullWidth
            value={localName}
            placeholder="Enter image name"
            onChange={(e) => setLocalName(e.target.value)}
            sx={{ mt: 1 }}
          />
        }
        buttons={[
          <CustomButton
            key='cancel'
            variant='outlined'
            text='Cancel'
            textColor='error'
            onClick={() => setRenameModalVisible(false)}
            disabled={isSubmitting}
            height={35}
          />,
          <CustomButton
            key='save'
            variant='filled'
            color='primary'
            text='Save'
            onClick={handleNameSave}
            loading={isSubmitting}
            height={35}
          />,
        ]} />
    </>
  );
}
